import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, ListGroup, ListGroupItem, Badge, Button, Input } from "reactstrap";
import { ClipLoader } from "react-spinners";
import apis from "../../../../Api";

function CourseModal({ isOpen, onClose, courseId = null, courseName = "Curso", companyId = null, onOpenUserDetail, onExport }) {
  const [searchValue, setSearchValue] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [students, setStudents] = React.useState([]);
  const [classes, setClasses] = React.useState([]);
  const [summary, setSummary] = React.useState({ studentsCount: 0, classesCount: 0 });

  React.useEffect(() => {
    let mounted = true;
    if (!isOpen || !courseId) return;
    setLoading(true);
    setError(null);
    setStudents([]);
    setClasses([]);
    setSummary({ studentsCount: 0, classesCount: 0 });

    (async () => {
      try {
        const resp = await apis.getCourseClasses(courseId, companyId).catch(() => null);
        const payload = resp?.data || resp || {};
        const studentsRaw = Array.isArray(payload.students) ? payload.students : [];
        const classesRaw = Array.isArray(payload.classes) ? payload.classes : [];
        if (!mounted) return;
        setStudents(studentsRaw);
        setClasses(classesRaw);
        setSummary({ studentsCount: studentsRaw.length, classesCount: classesRaw.length });
      } catch (err) {
        console.error('Erro ao carregar alunos do curso', err);
        if (!mounted) return;
        setError('Não foi possível carregar os alunos deste curso.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [isOpen, courseId, companyId]);

  const term = (searchValue || "").trim().toLowerCase();
  const filteredStudents = Array.isArray(students) ? students.filter(student => {
    if (!term) return true;
    const fields = [student.name, student.lastName, student.email, student.roleLabel];
    return fields.filter(Boolean).map(v => v.toString().toLowerCase()).some(v => v.includes(term));
  }) : [];

  const canExportCourse = !loading && !error && filteredStudents.length > 0;

  const handleExport = () => {
    if (!onExport) return;
    const rows = filteredStudents.map(student => ({
      id: student.id,
      name: student.name || "",
      lastName: student.lastName || "",
      email: student.email || "",
      role: student.roleLabel || "",
      blocked: student.isBlocked ? "Sim" : "Não"
    }));
    if (rows.length === 0) return;
    onExport(rows);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{courseName || "Curso"}</ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md="6" className="mb-3">
            <div className="text-sm text-muted">Alunos inscritos</div>
            <div className="h3 font-weight-bold mb-0">{summary?.studentsCount !== undefined ? summary.studentsCount : 0}</div>
          </Col>
          <Col md="6" className="mb-3">
            <div className="text-sm text-muted">Turmas utilizando o curso</div>
            <div className="h3 font-weight-bold mb-0">{summary?.classesCount !== undefined ? summary.classesCount : 0}</div>
          </Col>
        </Row>

        {loading && (
          <div className="py-5 text-center">
            <ClipLoader color="#5e72e4" size={32} />
            <div className="text-muted mt-3">Carregando alunos inscritos...</div>
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger" role="alert">{error}</div>
        )}

        {!loading && !error && (
          <>
            <h6 className="text-uppercase text-muted ls-1 mb-2">Alunos</h6>
            {Array.isArray(students) && students.length > 0 ? (
              <>
                <div className="mb-3">
                  <Input type="search" value={searchValue} onChange={e => setSearchValue(e.target.value)} placeholder="Pesquisar alunos por nome ou e-mail" />
                </div>
                {filteredStudents.length > 0 ? (
                  <ListGroup flush>
                    {filteredStudents.map(student => (
                      <ListGroupItem key={student.id} className="d-flex justify-content-between align-items-center py-2">
                        <div className="d-flex align-items-center">
                          <div className="mr-2" style={{ width: "40px", height: "40px", borderRadius: "50%", overflow: "hidden" }}>
                            <img src={student.photo || "/images/defaultAvatar.png"} alt="avatar" className="rounded-circle" style={{ width: "40px", height: "40px", objectFit: "cover" }} />
                          </div>
                          <div>
                            <div className="font-weight-medium">{student.name} {student.lastName}</div>
                            <div className="small text-muted">{student.email || "Sem e-mail"} · ID {student.id}</div>
                            <div className="small text-muted d-flex align-items-center">
                              <span>{student.roleLabel}</span>
                              <Badge color={student.isBlocked ? "danger" : "success"} pill className="ml-2">{student.statusLabel}</Badge>
                            </div>
                          </div>
                        </div>
                        <Button size="sm" color="primary" onClick={() => onOpenUserDetail && onOpenUserDetail(student)}>Ver</Button>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="text-center text-muted py-3">Nenhum aluno corresponde à pesquisa.</div>
                )}
              </>
            ) : (
              <div className="text-center text-muted py-4">Nenhum aluno inscrito neste curso para a unidade selecionada.</div>
            )}

            <h6 className="text-uppercase text-muted ls-1 mt-4 mb-2">Turmas</h6>
            {Array.isArray(classes) && classes.length > 0 ? (
              <ListGroup flush>
                {classes.map(cls => {
                  const companyLabel = cls.companyName || (cls.company ? `Unidade ${cls.company}` : null);
                  return (
                    <ListGroupItem key={cls.id} className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="font-weight-medium text-truncate" style={{ maxWidth: "220px" }}>{cls.name}</div>
                          <div className="small text-muted">{cls.countStudents || 0} alunos · {cls.countActive || 0} ativos</div>
                        </div>
                        {companyLabel ? <Badge color="primary" pill>{companyLabel}</Badge> : null}
                      </div>
                    </ListGroupItem>
                  );
                })}
              </ListGroup>
            ) : (
              <div className="text-center text-muted py-3">Nenhuma turma encontrada utilizando este curso.</div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <Button color="primary" onClick={handleExport} disabled={!canExportCourse}>Exportar CSV</Button>
        <Button color="secondary" onClick={onClose}>Fechar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default CourseModal;
